/**
 * Spindle Worker Hook
 *
 * Provides non-blocking access to the Spindle WASM reasoning engine.
 * Uses spindle-rust compiled to WASM via wasm-bindgen.
 */

import { useState, useEffect, useRef, useCallback } from "react";

// Types for worker communication (matching spindle-worker.js)
export interface Conclusion {
  literal: string;
  status: "+D" | "+d" | "-D" | "-d";
}

export interface MetaEntry {
  label: string;
  properties: Record<string, string | string[]>;
}

interface WorkerRequest {
  id: number;
  type: "init" | "reason";
  theory?: string;
}

interface WorkerResponse {
  id: number;
  type: "ready" | "result" | "error" | "stage";
  stage?: "wasm" | "reasoning" | "done";
  conclusions?: Conclusion[];
  meta?: MetaEntry[];
  error?: string;
}

type LoadingStage = "idle" | "wasm" | "reasoning" | "done";

interface UseSpindleWorkerResult {
  isLoading: boolean;
  loadingStage: LoadingStage;
  isReady: boolean;
  conclusions: Conclusion[];
  meta: MetaEntry[];
  error: string | null;
  reason: (theory: string) => void;
}

export function useSpindleWorker(): UseSpindleWorkerResult {
  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef(0);
  const pendingReasonRef = useRef(false); // Lock to prevent concurrent reasoning
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStage, setLoadingStage] = useState<LoadingStage>("idle");
  const [conclusions, setConclusions] = useState<Conclusion[]>([]);
  const [meta, setMeta] = useState<MetaEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Initialize worker
  useEffect(() => {
    // Use module worker for ES module imports (wasm-bindgen generated code)
    const worker = new Worker("/spindle-worker.js", { type: "module" });
    workerRef.current = worker;

    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const { type } = e.data;

      switch (type) {
        case "ready":
          setIsReady(true);
          setIsLoading(false);
          setLoadingStage("done");
          setError(null);
          break;

        case "stage":
          setLoadingStage(e.data.stage || "idle");
          if (e.data.stage !== "done") {
            setIsLoading(true);
          }
          break;

        case "result":
          pendingReasonRef.current = false; // Release lock
          setConclusions(e.data.conclusions || []);
          setMeta(e.data.meta || []);
          setIsLoading(false);
          setLoadingStage("done");
          break;

        case "error":
          pendingReasonRef.current = false; // Release lock
          console.error("[SpindleWorker] Error:", e.data.error);
          setError(e.data.error || "Unknown error");
          setIsLoading(false);
          break;
      }
    };

    worker.onerror = (err) => {
      console.error("[SpindleWorker] Worker error:", err);
      setError(err.message);
      setIsLoading(false);
    };

    // Initialize WASM in the worker
    setLoadingStage("wasm");
    worker.postMessage({ id: requestIdRef.current++, type: "init" } as WorkerRequest);

    return () => {
      worker.terminate();
    };
  }, []);

  const reason = useCallback((theory: string) => {
    if (workerRef.current && isReady && !pendingReasonRef.current) {
      pendingReasonRef.current = true; // Acquire lock
      setIsLoading(true);
      workerRef.current.postMessage({
        id: requestIdRef.current++,
        type: "reason",
        theory,
      } as WorkerRequest);
    }
  }, [isReady]);

  return {
    isLoading,
    loadingStage,
    isReady,
    conclusions,
    meta,
    error,
    reason,
  };
}
